import { abbreviateLastName } from './personName';

export type ApplicationFieldRow = {
    label: string;
    value: string;
};

export type ApplicationSection = {
    title: string;
    rows: ApplicationFieldRow[];
};

const SECTION_ORDER: { title: string; labels: string[] }[] = [
    {
        title: 'Placement',
        labels: ['Preferences', 'Open to other prefs', 'Older puppy / adult', 'Plans'],
    },
    {
        title: 'Home',
        labels: ['Housing', 'Yard', 'Landlord', 'Time spent', 'Indoor / outdoor', 'Hours alone'],
    },
    {
        title: 'Household',
        labels: [
            'First dog',
            'First puppy',
            'Other pets',
            'Grooming',
            'Adults agree',
            'Household ages',
            'Allergies',
            'Training',
            'Crate',
            'Gift',
        ],
    },
    {
        title: 'Timeline',
        labels: [
            'How soon',
            'Pickup age',
            'Austin pickup',
            'Commitment',
            'Follow-up',
            'Rehome through us',
        ],
    },
    {
        title: 'About',
        labels: ['Name', 'City', 'Why', 'Heard about us', 'Other info', 'Questions'],
    },
];

const TEASER_LABELS = ['Preferences', 'Plans', 'How soon', 'Austin pickup', 'Housing', 'City'];

export const parseApplicationSummary = (summary: string): ApplicationFieldRow[] =>
    String(summary || '')
        .split('\n')
        .map((line) => {
            const index = line.indexOf(': ');
            if (index <= 0) {
                return null;
            }
            const label = line.slice(0, index).trim();
            const value = line.slice(index + 2).trim();
            return label && value ? { label, value } : null;
        })
        .filter((row): row is ApplicationFieldRow => Boolean(row));

const HIDDEN_ADDRESS_LABEL = /^(street|address|zip|postal|country)$/i;

export const applicationRowsFromInvite = (application?: {
    fields?: ApplicationFieldRow[];
    summary?: string;
}): ApplicationFieldRow[] => {
    const rows =
        application?.fields && application.fields.length
            ? application.fields.filter((row) => row.label && row.value)
            : parseApplicationSummary(application?.summary || '');
    return rows
        .filter((row) => !HIDDEN_ADDRESS_LABEL.test(row.label))
        .map((row) =>
            row.label === 'Name' ? { ...row, value: abbreviateLastName(row.value) } : row
        );
};

export const groupApplicationSections = (rows: ApplicationFieldRow[]): ApplicationSection[] => {
    const used = new Set<string>();
    const sections = SECTION_ORDER.map((section) => {
        const matched = section.labels
            .map((label) => rows.find((row) => row.label === label))
            .filter((row): row is ApplicationFieldRow => Boolean(row));
        matched.forEach((row) => used.add(row.label));
        return { title: section.title, rows: matched };
    }).filter((section) => section.rows.length);

    const leftover = rows.filter((row) => !used.has(row.label));
    if (leftover.length) {
        sections.push({ title: 'More', rows: leftover });
    }
    return sections;
};

export const applicationTeasers = (rows: ApplicationFieldRow[]): string[] =>
    TEASER_LABELS.map((label) => rows.find((row) => row.label === label)?.value)
        .filter((value): value is string => Boolean(value))
        .slice(0, 4);
